/**
 * Opens and closes the app's modal `<dialog>` elements. The declarative
 * triggers in `boot/main/globalSetup/reveal` come through here, and so does
 * feature code that opens a dialog of its own.
 */

import { acquireScrollLock, releaseScrollLock } from 'common/scrollLock';

/**
 * `op_mini`, `and_uc` and `kaios` are inside the browserslist targets and have
 * no `<dialog>` at all, where the constructor is undefined and every
 * `instanceof HTMLDialogElement` throws rather than returning false.
 */
export const SUPPORTS_DIALOG = typeof HTMLDialogElement !== 'undefined';

export function isDialog(value: unknown): value is HTMLDialogElement {
  return SUPPORTS_DIALOG && value instanceof HTMLDialogElement;
}

function dialogById(id: string): HTMLDialogElement | null {
  const element = document.getElementById(id);
  return isDialog(element) ? element : null;
}

export function openDialog(id: string): void {
  const dialog = dialogById(id);
  if (dialog) openModal(dialog);
}

export function closeDialog(id: string): void {
  const dialog = dialogById(id);
  if (dialog?.open) dialog.close();
}

/**
 * `showModal()` makes the page behind the dialog inert, not unscrollable —
 * separate properties, and the top layer keeps the dialog still while the page
 * slides behind it. The lock is shared with the React modals through
 * `common/scrollLock`, so both kinds hold the page the same way and neither
 * releases what the other is still holding. It costs the scrollbar, so the
 * page shifts 15px sideways.
 *
 * Desktop only. Mobile Safari ignores `overflow: hidden` on `body`, which is
 * why `BodyScrollPreventer` carries `touchmove` handlers this does not match.
 *
 * Each dialog is its own holder, and its `close` event is what releases it —
 * one per successful open, since this returns early on a dialog that is
 * already open.
 */
export function openModal(dialog: HTMLDialogElement): void {
  // The top layer stacks: a second `showModal()` paints over the first with
  // both backdrops live, and closing the top brings the first back. One modal
  // at a time is the contract every surface assumes, and the login and
  // register dialogs each hold a trigger for the other, so opening over an
  // open dialog is a routine path, not an edge case.
  document.querySelectorAll('dialog[open]').forEach(other => {
    if (other !== dialog && isDialog(other)) other.close();
  });

  if (dialog.open) return;
  dialog.showModal();
  acquireScrollLock(dialog);
  releaseOnClose(dialog);
}

/**
 * One listener per dialog, kept for its lifetime. A listener per open would
 * not do: a dialog reopened before the `close` of its first open lands carries
 * two of them, both see the dialog open again on that late event, and the
 * close that follows finds none left.
 */
const releasing = new WeakSet<HTMLDialogElement>();

function releaseOnClose(dialog: HTMLDialogElement): void {
  if (releasing.has(dialog)) return;
  releasing.add(dialog);
  dialog.addEventListener('close', () => {
    // `close` is delivered from a queued task, so a dialog reopened before it
    // lands already holds a lock that reopen took. Releasing on its behalf
    // here leaves an open modal over a page that still scrolls.
    if (!dialog.open) releaseScrollLock(dialog);
  });
}
